import type { HTMLAttributes, KeyboardEvent, ReactNode } from "react";

import { ForbiddenError } from "../lib/forbiddenError";
import { Button } from "./ui";

export function PageHeader({
  className,
  ...props
}: HTMLAttributes<HTMLElement>) {
  return (
    <header
      {...props}
      className={["workspace-page-header", className].filter(Boolean).join(" ")}
    />
  );
}

export function Panel({
  title,
  meta,
  children,
  className,
}: {
  title?: ReactNode;
  meta?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={["panel", className].filter(Boolean).join(" ")}>
      {(title || meta) && (
        <header className="panel__header">
          {title && <h2>{title}</h2>}
          {meta && <div className="panel__meta">{meta}</div>}
        </header>
      )}
      <div className="panel__body">{children}</div>
    </section>
  );
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="state" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" /> {label}
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
}: {
  error: Error;
  onRetry?: () => void;
}) {
  if (error instanceof ForbiddenError) {
    return (
      <div className="state state--error" role="alert">
        <div>Your account&rsquo;s role doesn&rsquo;t allow this action.</div>
        <p className="state-copy">
          This workspace requires the <b>launcher</b> or <b>admin</b> role — ask
          your Isara engagement team to adjust your access.
        </p>
      </div>
    );
  }
  return (
    <div className="state state--error" role="alert">
      <div>Something went wrong.</div>
      <code className="state-detail">{error.message}</code>
      {onRetry && <Button onClick={onRetry}>Retry</Button>}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="state state--empty">{children}</div>;
}

export function InlineNotice({
  children,
  tone = "info",
}: {
  children: ReactNode;
  tone?: "info" | "warning" | "danger";
}) {
  return (
    <div
      className={`inline-notice inline-notice--${tone}`}
      role={tone === "danger" ? "alert" : "status"}
    >
      {children}
    </div>
  );
}

interface SegmentOption<Value extends string> {
  value: Value;
  label: string;
}

function nextSegmentIndex(
  current: number,
  key: string,
  count: number,
): number | null {
  if (count === 0) return null;
  if (key === "Home") return 0;
  if (key === "End") return count - 1;
  if (key === "ArrowRight" || key === "ArrowDown") return (current + 1) % count;
  if (key === "ArrowLeft" || key === "ArrowUp") {
    return (current - 1 + count) % count;
  }
  return null;
}

export function SegmentedControl<Value extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: Value;
  options: SegmentOption<Value>[];
  onChange: (value: Value) => void;
}) {
  const handleKey = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    const next = nextSegmentIndex(index, event.key, options.length);
    if (next === null) return;
    event.preventDefault();
    const option = options[next];
    if (!option) return;
    onChange(option.value);
    event.currentTarget.parentElement
      ?.querySelectorAll<HTMLButtonElement>("button")
      .item(next)
      .focus();
  };

  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((option, index) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          className={option.value === value ? "is-active" : undefined}
          onClick={() => onChange(option.value)}
          onKeyDown={(event) => handleKey(event, index)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
